import { useState, type FormEvent } from 'react';
import type { ApprovalMode } from '@tailorcast/shared-types';
import { useCreateTask } from '../hooks/useTaskMutations';
import { useNavigate } from 'react-router-dom';

export function TaskBuilder() {
  const navigate = useNavigate();
  const createTask = useCreateTask();

  const [goal, setGoal] = useState('');
  const [maxProducts, setMaxProducts] = useState('');
  const [maxScore, setMaxScore] = useState('');
  const [tone, setTone] = useState('');
  const [preserveHtml, setPreserveHtml] = useState(true);
  const [approvalMode, setApprovalMode] = useState<ApprovalMode>('manual');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const result = await createTask.mutateAsync({
      goal,
      marketplace: 'bitrix',
      source: 'bitrix',
      scope: {
        maxProducts: maxProducts ? parseInt(maxProducts) : undefined,
        scoreFilter: maxScore ? { max: parseFloat(maxScore) } : undefined,
      },
      constraints: {
        tone: tone || undefined,
        preserveHtml,
      },
      approvalMode,
    });

    navigate(`/tasks/${result.id}`);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="goal" className="block text-sm font-medium text-gray-700 mb-1">
          Goal
        </label>
        <textarea
          id="goal"
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          required
          rows={3}
          className="input"
          placeholder="e.g. Fix SEO for the 15 worst-scoring products"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="maxProducts" className="block text-sm font-medium text-gray-700 mb-1">
            Max Products
          </label>
          <input
            id="maxProducts"
            type="number"
            value={maxProducts}
            onChange={(e) => setMaxProducts(e.target.value)}
            className="input"
            placeholder="e.g. 15"
          />
        </div>
        <div>
          <label htmlFor="maxScore" className="block text-sm font-medium text-gray-700 mb-1">
            Max Score Filter
          </label>
          <input
            id="maxScore"
            type="number"
            step="0.1"
            value={maxScore}
            onChange={(e) => setMaxScore(e.target.value)}
            className="input"
            placeholder="e.g. 3.0"
          />
        </div>
      </div>

      <div>
        <label htmlFor="tone" className="block text-sm font-medium text-gray-700 mb-1">
          Tone (optional)
        </label>
        <input
          id="tone"
          type="text"
          value={tone}
          onChange={(e) => setTone(e.target.value)}
          className="input"
          placeholder="e.g. professional, casual, luxury"
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          id="preserveHtml"
          type="checkbox"
          checked={preserveHtml}
          onChange={(e) => setPreserveHtml(e.target.checked)}
          className="rounded border-gray-300"
        />
        <label htmlFor="preserveHtml" className="text-sm text-gray-700">
          Preserve HTML formatting
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Approval Mode</label>
        <div className="flex gap-3">
          {(['manual', 'assisted'] as const).map((mode) => (
            <label key={mode} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="approvalMode"
                value={mode}
                checked={approvalMode === mode}
                onChange={() => setApprovalMode(mode)}
                className="text-tailorcast-primary"
              />
              <span className="text-sm text-gray-700 capitalize">{mode}</span>
            </label>
          ))}
        </div>
      </div>

      {createTask.error && (
        <div className="text-sm rounded-lg p-3 bg-red-50 border border-red-200 text-red-700">
          {createTask.error instanceof Error ? createTask.error.message : 'Failed to create task'}
        </div>
      )}

      <button type="submit" disabled={createTask.isPending || !goal} className="btn btn-primary">
        {createTask.isPending ? 'Creating...' : 'Create Task'}
      </button>
    </form>
  );
}
